'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Form } from '../../../components/ui/form'
import { CustomButton } from '@/components/custom/CustomButton'
import { useState, useTransition } from 'react'
import { ContactSchema } from '@/lib/schemas/public-schemas'
import { sendContactEmail } from '../_actions/contactActions'
import { InputFormField } from '@/components/form/InputFormField'
import { TextareaFormField } from '@/components/form/TextareaFormField'
import { ErrorForm } from '@/app/auth/_components/ErrorForm'
import { SuccessForm } from '@/app/auth/_components/SuccessForm'
import Lottie from 'lottie-react'
import animationApproved from '../../../lib/animations/approved_1.json'
import { SwitchFormField } from '@/components/form/SwitchFormField'
import { PrivacyPolicyDialog } from './PrivacyPolicyDialog'
import { COLORS } from '@/lib/config'
import { useCurrentUser } from '@/lib/auth/useSessionCS'
import { SelectFormField } from '@/components/form/SelectFormField'

export const ContactForm = () => {
    const user = useCurrentUser()
    const [isPending, startTransition] = useTransition()
    const [error, setError] = useState<string | undefined>('')
    const [success, setSuccess] = useState<boolean>(false)
    const topicSelectOptions = [
        { value: 'questions', label: 'Questions' },
        { value: 'problem', label: 'Problem' },
        { value: 'feedback', label: 'Feedback' }
    ]
    const form = useForm<z.infer<typeof ContactSchema>>({
        resolver: zodResolver(ContactSchema),
        defaultValues: {
            name: user?.name ? `${user?.name}` : '',
            email: user?.email ? `${user?.email}` : '',
            message: '',
            topicSelection: 'questions',
            privacyPolicy: false
        }
    })

    const { watch } = form
    // eslint-disable-next-line react-hooks/incompatible-library
    const privacyAccepted = watch('privacyPolicy')

    const onSubmit = (values: z.infer<typeof ContactSchema>) => {
        setError('')
        setSuccess(false)
        startTransition(() => {
            sendContactEmail(values).then((data) => {
                if (data?.success) {
                    setSuccess(true)
                } else {
                    setError(data?.error?.toString())
                }
            })
        })
    }

    return (
        <div className='w-full'>
            {success ? (
                <section className='flex flex-col items-center justify-center space-y-6 text-center sm:space-y-8'>
                    <Lottie
                        loop={false}
                        animationData={animationApproved}
                        className='mx-auto h-auto w-full max-w-54 sm:max-w-72'
                        aria-hidden
                    />
                    <div className='max-w-prose space-y-2'>
                        <h2 className='text-lg font-semibold leading-snug text-foreground sm:text-xl'>
                            Thank you! We received your support request.
                        </h2>
                        <p className='text-sm leading-relaxed text-muted-foreground sm:text-base'>
                            A confirmation has been sent to your email. Our team will respond within 24 hours.
                        </p>
                    </div>
                </section>
            ) : (
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-8'>
                        <div className='space-y-6 sm:space-y-7' role='group' aria-label='Your message'>
                            <InputFormField form={form} disabled={isPending} name='name' placeholder='John Doe' />
                            <InputFormField
                                form={form}
                                disabled={isPending}
                                name='email'
                                type='email'
                                placeholder='john.doe@gmail.com'
                            />
                            <SelectFormField
                                form={form}
                                name='topicSelection'
                                items={Object.values(topicSelectOptions).map((topic) => ({
                                    id: topic.value,
                                    description: topic.label
                                }))}
                                placeholder='Select a topic'
                                type='select'
                            />
                            <TextareaFormField
                                form={form}
                                disabled={isPending}
                                name='message'
                                placeholder='Your question or feedback…'
                            />
                            <SwitchFormField
                                form={form}
                                name='privacyPolicy'
                                label='Privacy Policy'
                                disabled={isPending}
                                description={
                                    <>
                                        I accept the <PrivacyPolicyDialog labelColor={COLORS.dark} />
                                    </>
                                }
                            />
                        </div>
                        <ErrorForm message={error} />
                        <SuccessForm message={success ? 'Message sent successfully!' : ''} />
                        <CustomButton
                            disabled={isPending || !privacyAccepted}
                            type='submit'
                            className='w-full'
                            size='lg'>
                            Send message
                        </CustomButton>
                    </form>
                </Form>
            )}
        </div>
    )
}
